import JsPDF from 'jspdf';
import { Contract } from '../api/contractService';

const PAGE_MARGIN = 18;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const PAGE_BOTTOM = 278;

const formatMoney = (amount: string | number | undefined) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(amount || 0));

export const openContractPdf = (contract: Contract): void => {
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) throw new Error('Allow pop-ups to view the contract PDF.');

  const pdf = new JsPDF({ unit: 'mm', format: 'a4' });
  const contractFromName = contract.proposal?.contract_from_company?.name || 'Torch Marketing Co Ltd';
  let y = 20;

  const ensureSpace = (height: number) => {
    if (y + height > PAGE_BOTTOM) {
      pdf.addPage();
      y = 20;
    }
  };

  const writeLines = (
    text: string,
    options: { fontSize?: number; bold?: boolean; gapAfter?: number } = {}
  ) => {
    const fontSize = options.fontSize ?? 10;
    const lineHeight = fontSize * 0.42;
    pdf.setFont('helvetica', options.bold ? 'bold' : 'normal');
    pdf.setFontSize(fontSize);

    text.replace(/\r\n/g, '\n').split('\n').forEach((paragraph) => {
      const lines = paragraph ? pdf.splitTextToSize(paragraph, CONTENT_WIDTH) : [''];
      lines.forEach((line: string) => {
        ensureSpace(lineHeight);
        pdf.text(line, PAGE_MARGIN, y);
        y += lineHeight;
      });
    });
    y += options.gapAfter ?? 2;
  };

  pdf.setProperties({
    title: `Magazine advertising contract - ${contract.company?.name || ''}`,
    subject: 'Magazine advertising contract',
    author: contractFromName,
  });

  writeLines('MAGAZINE ADVERTISING CONTRACT', { fontSize: 18, bold: true, gapAfter: 4 });
  writeLines(`Contract from: ${contractFromName}`, { bold: true, gapAfter: 1 });
  writeLines(`Company: ${contract.company?.name || '-'}`, { bold: true, gapAfter: 1 });
  writeLines(`Contract reference: ${contract.id}`, { gapAfter: 1 });
  writeLines(`Status: ${contract.status.toUpperCase()}`, { gapAfter: 5 });

  if (contract.proposal?.line_items?.length) {
    writeLines('ORDER SUMMARY', { fontSize: 13, bold: true, gapAfter: 3 });
    contract.proposal.line_items.forEach((item, index) => {
      const description = [
        item.ad_slot?.issue?.publication?.name,
        item.ad_slot?.issue?.title,
        item.label,
        item.size,
      ].filter(Boolean).join(' | ');
      writeLines(`${index + 1}. ${description}`, { bold: true, gapAfter: 0.5 });
      writeLines(`Price: ${formatMoney(item.price)}`, { gapAfter: 2 });
    });
    const total = contract.proposal.line_items.reduce((sum, item) => sum + Number(item.price || 0), 0);
    writeLines(`Total: ${formatMoney(total)}`, { fontSize: 12, bold: true, gapAfter: 6 });
  }

  writeLines('CONTRACT', { fontSize: 13, bold: true, gapAfter: 3 });
  writeLines(contract.body || 'No contract content was supplied.', { gapAfter: 7 });

  if (contract.terms_and_conditions) {
    writeLines('TERMS & CONDITIONS', { fontSize: 13, bold: true, gapAfter: 3 });
    writeLines(contract.terms_and_conditions, { gapAfter: 7 });
  }

  writeLines('SIGNATURE', { fontSize: 13, bold: true, gapAfter: 3 });
  if (contract.status === 'signed') {
    writeLines(`Electronically signed by: ${contract.signer_name || '-'}`, { bold: true, gapAfter: 1 });
    if (contract.signer_email) writeLines(`Email: ${contract.signer_email}`, { gapAfter: 1 });
    if (contract.signed_at) writeLines(`Signed: ${new Date(contract.signed_at).toLocaleString('en-GB')}`, { gapAfter: 1 });
  } else {
    writeLines('Name: __________________________________________', { gapAfter: 4 });
    writeLines('Signature: _______________________________________', { gapAfter: 4 });
    writeLines('Date: ___________________________________________', { gapAfter: 1 });
  }

  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(110);
    pdf.text(`Contract ${contract.id} | Page ${page} of ${pageCount}`, PAGE_MARGIN, 290);
  }

  previewWindow.location.href = pdf.output('bloburl').toString();
};
